import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    cancelAppointment,
    createAppointment,
    fetchMyAppointments,
    updateAppointment,
    type Appointment,
    type CancelAppointmentInput,
    type CreateAppointmentInput,
    type UpdateAppointmentInput
} from '../services/appointments';
import { useRecentActions } from '../contexts/RecentActionsContext';
import { useToast } from '../providers/ToastProvider';
import { useUser } from '../providers/UserProvider';
import { notesQueryKey } from './usePatientNotes';

export const APPOINTMENTS_QUERY_KEY = ['appointments', 'mine'] as const;

function extractErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof Error && error.message) {
        return error.message;
    }
    return fallback;
}

export function useAppointmentsMine() {
    const { user } = useUser();
    const practiceIds = user?.database?.data?.practices;

    const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
        queryKey: APPOINTMENTS_QUERY_KEY,
        queryFn: fetchMyAppointments,
        enabled: !!practiceIds?.length,
        staleTime: 60 * 1000
    });

    return {
        appointments: data ?? ([] as Appointment[]),
        isLoading,
        isFetching,
        isError,
        error,
        refetch
    };
}

export function useCreateAppointment() {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    const { markActionCompleted } = useRecentActions();

    return useMutation({
        mutationFn: (input: CreateAppointmentInput) => createAppointment(input),
        // Awaited (not fire-and-forget): `invalidateQueries` returns a
        // promise that resolves once the invalidated *active* queries have
        // actually refetched. Without awaiting it here, `mutateAsync()` (and
        // therefore `AppointmentsModal.handleSave`) would resolve before the
        // appointments list cache was refreshed, closing the composer and
        // letting an immediate reopen of the same appointment read the
        // pre-save `noteId`/`hasNote` off stale data. See the appointments
        // modal bug where a just-added chart note appeared to vanish on
        // reopen: the note persisted correctly server-side, this timing gap
        // was the actual cause.
        onSuccess: async (result, variables) => {
            await queryClient.invalidateQueries({ queryKey: APPOINTMENTS_QUERY_KEY });
            // A brand-new appointment can resolve the patient's pending
            // 'Schedule appointment' action (or just change their
            // requiredAction/next-appointment display generally) - refresh
            // both the list and the single-patient caches so the patients
            // table picks it up without waiting for an unrelated refetch.
            await queryClient.invalidateQueries({ queryKey: ['patients'] });
            await queryClient.invalidateQueries({ queryKey: ['patient', variables.patientId] });
            if (variables.note) {
                await queryClient.invalidateQueries({
                    queryKey: notesQueryKey(variables.patientId)
                });
            }
            // Only a genuine hold-exit counts as "resolved the required
            // action" for sticky-sort purposes - a same-status appointment
            // add (no hold to exit) shouldn't lock the row in place.
            if (result.exitedHold) {
                markActionCompleted(variables.patientId, 'Schedule appointment');
            }
            showToast('Appointment added', 'success');
            return result;
        },
        onError: (error: unknown) => {
            showToast(extractErrorMessage(error, 'Failed to add appointment'), 'error');
        }
    });
}

export function useUpdateAppointment() {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: (input: UpdateAppointmentInput) => updateAppointment(input),
        // Awaited for the same reason as useCreateAppointment's onSuccess:
        // mutateAsync() must not resolve (and let the composer close) until
        // the appointments list cache actually reflects the save, or an
        // immediate reopen of the same appointment reads stale
        // noteId/hasNote and looks like the note vanished.
        onSuccess: async (_result, variables) => {
            await queryClient.invalidateQueries({ queryKey: APPOINTMENTS_QUERY_KEY });
            // `!== undefined` (not truthy) so an explicit `note: null` clear
            // also refreshes the notes query: a note was deleted, same as
            // a note being created/updated.
            if (variables.note !== undefined) {
                await queryClient.invalidateQueries({
                    queryKey: notesQueryKey(variables.patientId)
                });
            }
            showToast('Appointment updated', 'success');
        },
        onError: (error: unknown) => {
            showToast(extractErrorMessage(error, 'Failed to update appointment'), 'error');
        }
    });
}

export function useCancelAppointment() {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    return useMutation({
        mutationFn: (input: CancelAppointmentInput) => cancelAppointment(input),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: APPOINTMENTS_QUERY_KEY });
            showToast('Appointment canceled', 'success');
        },
        onError: (error: unknown) => {
            showToast(extractErrorMessage(error, 'Failed to cancel appointment'), 'error');
        }
    });
}
